
export const QUALITY_CONFIG = {
  PING_COUNT: 5,         // Number of round-trips to measure
  PING_INTERVAL_MS: 200, // Delay between consecutive pings (ms)
  TIMEOUT_MS: 5000,      // Max wait for a single ping acknowledgement (ms)
  MAX_RTT_MS: 250,       // Fail if average RTT exceeds this value (ms)
  MAX_JITTER_MS: 100,    // Fail if jitter (std dev) exceeds this value (ms)
  // Packet-loss threshold is implicit: need at least 3 out of PING_COUNT valid
};

const TAG = "[ConnectionQuality]";

/**
 * Measures connection quality to the server.
 * @param {Socket} socket - Connected socket.io instance
 * @returns {Promise<QualityResult>}
 */
export async function measureConnectionQuality(socket) {
  return new Promise((resolve) => {
    console.log(
      `${TAG} Starting test — 1 warmup + ${QUALITY_CONFIG.PING_COUNT} pings @ ${QUALITY_CONFIG.PING_INTERVAL_MS}ms apart`
    );
    console.log(
      `${TAG} Thresholds → Avg RTT < ${QUALITY_CONFIG.MAX_RTT_MS}ms | Jitter < ${QUALITY_CONFIG.MAX_JITTER_MS}ms | min 3/${QUALITY_CONFIG.PING_COUNT} pings valid`
    );

    // Send one warmup ping first to let the socket path stabilise
    // (first ping is typically slow due to connection setup overhead)
    socket.timeout(QUALITY_CONFIG.TIMEOUT_MS).emit("latency:ping", performance.now(), () => {
      console.log(`${TAG} Warmup ping done — starting measurement`);

      const samples = [];
      let completed = 0;

      const runPing = () => {
        const pingNumber = completed + 1;
        const start = performance.now();

        socket
          .timeout(QUALITY_CONFIG.TIMEOUT_MS)
          .emit("latency:ping", start, (err) => {
            if (err) {
              console.log(`${TAG} Ping ${pingNumber}: TIMEOUT/ERROR`);
              samples.push(null);
            } else {
              const rtt = performance.now() - start;
              console.log(`${TAG} Ping ${pingNumber}: ${Math.round(rtt)}ms`);
              samples.push(rtt);
            }

            completed++;

            if (completed < QUALITY_CONFIG.PING_COUNT) {
              setTimeout(runPing, QUALITY_CONFIG.PING_INTERVAL_MS);
            } else {
              const result = analyzeResults(samples);
              logSummary(result);
              resolve(result);
            }
          });
      };

      setTimeout(runPing, QUALITY_CONFIG.PING_INTERVAL_MS);
    });
  });
}

function analyzeResults(samples) {
  const validSamples = samples.filter((s) => s !== null);
  const lossPercent =
    ((samples.length - validSamples.length) / samples.length) * 100;

  if (validSamples.length < 3) {
    return {
      passed: false,
      reason: "Too many dropped packets — unstable connection",
      rtt: null,
      jitter: null,
      lossPercent,
      samples: [],
    };
  }

  const avgRtt = validSamples.reduce((a, b) => a + b, 0) / validSamples.length;

  const variance =
    validSamples.reduce((sum, s) => sum + Math.pow(s - avgRtt, 2), 0) /
    validSamples.length;
  const jitter = Math.sqrt(variance);

  const passed =
    avgRtt < QUALITY_CONFIG.MAX_RTT_MS && jitter < QUALITY_CONFIG.MAX_JITTER_MS;

  return {
    passed,
    rtt: Math.round(avgRtt),
    jitter: Math.round(jitter),
    lossPercent,
    samples: validSamples.map((s) => Math.round(s)),
    reason: !passed
      ? `Avg RTT: ${Math.round(avgRtt)}ms (max: ${QUALITY_CONFIG.MAX_RTT_MS}ms), ` +
        `Jitter: ${Math.round(jitter)}ms (max: ${QUALITY_CONFIG.MAX_JITTER_MS}ms)`
      : null,
  };
}

function logSummary(result) {
  console.log(`${TAG} ─────────────────────────────────────────`);

  if (result.rtt !== null) {
    const rttStatus = result.rtt < QUALITY_CONFIG.MAX_RTT_MS ? "✓" : "✗";
    const jitterStatus =
      result.jitter < QUALITY_CONFIG.MAX_JITTER_MS ? "✓" : "✗";

    console.log(
      `${TAG} Avg RTT:    ${String(result.rtt).padStart(4)}ms  (max: ${QUALITY_CONFIG.MAX_RTT_MS}ms) ${rttStatus}`
    );
    console.log(
      `${TAG} Jitter:     ${String(result.jitter).padStart(4)}ms  (max: ${QUALITY_CONFIG.MAX_JITTER_MS}ms) ${jitterStatus}`
    );
  }

  console.log(
    `${TAG} Loss:       ${result.lossPercent.toFixed(0)}%    (min valid: 3/${QUALITY_CONFIG.PING_COUNT})`
  );
  console.log(`${TAG} ─────────────────────────────────────────`);

  if (result.passed) {
    console.log(`${TAG} RESULT: PASSED ✓`);
  } else {
    console.warn(`${TAG} RESULT: FAILED ✗ — ${result.reason}`);
  }
}